import React, { useState, useEffect } from 'react'
import Breadcrum from '../../components/breadcrum'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
  getFinancialById,
  updateFinancial,
  getSingleFinancialReportsById,
} from '../../api/financialApi'
import { getCleanPdfName } from '../../handleDateAndPdf'
import { FaArrowLeft } from 'react-icons/fa'

const EditFinancialReport = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { reportId } = useParams()
  const [pdfFile, setPdfFile] = useState(null)
  const [pdfFileNew, setPdfFileNew] = useState(null)
  const [pdfPreview, setPdfPreview] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [data, setData] = useState({
    companyName: '',
    cin: '',
  })

  useEffect(() => {
    document.title = 'Edit Financial Report'
  })

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      financialYear: '',
      reportTitle: '',
      reportDesc: '',
      reportPrice: '',
      report: null,
    },
  })

  // Fixed: Removed deprecated onSuccess callback
  const {
    data: singleReport,
    isLoading: isReportsLoading,
    isError: isReportsError,
    error: reportsError,
  } = useQuery({
    queryKey: ['singleReport', reportId],
    queryFn: () => getSingleFinancialReportsById(reportId),
    enabled: !!reportId,
  })

  const populateSingleReport = (data) => {
    if (!data) return

    setValue('financialYear', data.financialYear)
    setValue('reportTitle', data.reportTitle)
    setValue('reportDesc', data.reportDesc)
    setValue('reportPrice', data.reportPrice)

    if (data.reportUrl) {
      const cleanName = getCleanPdfName(data.reportUrl)
      setPdfFile(cleanName)
      // Set initial PDF preview if available
      setPdfPreview(data.reportUrl)
    }
  }

  useEffect(() => {
    if (singleReport) {
      populateSingleReport(singleReport)
    }
  }, [singleReport])

  // Fixed: Removed deprecated onSuccess callback
  const {
    data: financialData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['financialData', id],
    queryFn: () => getFinancialById(id),
    enabled: !!id,
  })

  const populateData = (data) => {
    if (!data) return

    setData((prev) => ({
      ...prev,
      companyName: data.companyName,
      cin: data.cin,
    }))

    if (data.companyLogoUrl) {
      setImagePreview(data.companyLogoUrl)
    }
  }

  useEffect(() => {
    if (financialData) {
      populateData(financialData)
    }
  }, [financialData])

  // Create mutation
  const updateMutation = useMutation({
    mutationFn: (formData) => updateFinancial(reportId, formData),
    onSuccess: () => {
      reset()
      toast.success('Report updated successfully!')
      navigate('/dealdashboard/financial')
    },
    onError: (error) => {
      console.error('Error details:', error)
      toast.error(error.response?.data?.message || 'Failed to update report')
    },
  })

  useEffect(() => {
    const pdf = watch('report')

    // Handle PDF preview
    if (pdf && pdf[0]) {
      const previewUrl = URL.createObjectURL(pdf[0])
      setPdfPreview(previewUrl)
      setPdfFileNew(pdf[0])

      // Cleanup function
      return () => URL.revokeObjectURL(previewUrl)
    }
  }, [watch('report')])

  const handlePdfChange = (e) => {
    const files = e.target.files
    if (files && files.length > 0) {
      setPdfFileNew(files[0]) // Fixed: Set single file, not FileList
      setValue('report', files)
    }
  }

  const onSubmit = (data) => {
    const formData = new FormData()

    // Append basic fields
    formData.append('financialYear', data.financialYear)
    formData.append('reportTitle', data.reportTitle) // Fixed: Added missing reportTitle
    formData.append('reportPrice', parseInt(data.reportPrice) || 0)
    formData.append('reportDesc', data.reportDesc)

    if (data.report && data.report.length > 0) {
      formData.append('report', data.report[0])
    }

    // Log FormData contents for debugging
    console.log('FormData contents:')
    for (let [key, value] of formData.entries()) {
      console.log(key, value)
    }

    updateMutation.mutate(formData)
  }

  // Loading state
  if (isLoading || isReportsLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    )
  }

  // Error state
  if (isError || isReportsError) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-red-500">
          Error loading data. Please try again.
        </div>
      </div>
    )
  }

  return (
    <>
      <Breadcrum
        heading="Financial Reports"
        subheading="Edit Financial Report"
      />

      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex justify-center items-center bg-gray-500 p-2 text-white rounded sticky -top-[15px]"
      >
        <FaArrowLeft />
        &nbsp; Back
      </button>

      <div className="max-w-8xl text-left mx-auto px-4 py-6">
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Company Information Section */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white p-6 rounded-xl shadow">
              <h2 className="font-semibold text-gray-800 mb-2">Company Name</h2>
              <input
                type="text"
                placeholder="Enter company name"
                value={data.companyName}
                readOnly
                className="border border-gray-300 rounded-lg p-3 w-full text-base focus:border-orange-500 focus:ring-orange-500 focus:outline-none bg-slate-300"
              />
            </div>

            <div className="bg-white p-6 rounded-xl shadow">
              <h2 className="font-semibold text-gray-800 mb-2">
                Company CIN Number
              </h2>
              <input
                type="text"
                placeholder="Enter CIN number"
                value={data.cin}
                readOnly
                className="border border-gray-300 rounded-lg p-3 w-full text-base focus:border-orange-500 focus:ring-orange-500 focus:outline-none bg-slate-300"
              />
            </div>

            <div className="bg-white p-6 rounded-xl shadow">
              <h2 className="font-semibold text-gray-800 mb-2">
                Year Of Report
              </h2>
              <input
                type="text"
                placeholder="Enter report year (e.g., 2023-24)"
                className={`border ${
                  errors.financialYear ? 'border-red-500' : 'border-gray-300'
                } rounded-lg p-3 w-full text-base focus:border-orange-500 focus:ring-orange-500 focus:outline-none`}
                {...register('financialYear', {
                  required: 'Financial year is required',
                })}
              />
              {errors.financialYear && (
                <p className="mt-1 text-red-500 text-sm">
                  {errors.financialYear.message}
                </p>
              )}
            </div>

            <div className="bg-white p-6 rounded-xl shadow">
              <h2 className="font-semibold text-gray-800 mb-2">Company Logo</h2>
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="mt-4 max-h-64 object-cover rounded-lg border border-gray-200"
                />
              )}
            </div>
          </div>

          {/* Reports Section */}
          <div className="mb-6">
            <div className="mb-6 p-6 bg-gray-50 rounded-xl shadow">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h4 className="font-semibold text-gray-800 mb-2">
                    Report Title
                  </h4>
                  <input
                    type="text"
                    placeholder="Enter report title"
                    className={`border ${
                      errors.reportTitle ? 'border-red-500' : 'border-gray-300'
                    } rounded-lg p-3 w-full text-base focus:border-orange-500 focus:ring-orange-500 focus:outline-none`}
                    {...register('reportTitle', {
                      required: 'Report title is required',
                    })}
                  />
                  {errors.reportTitle && (
                    <p className="mt-1 text-red-500 text-sm">
                      {errors.reportTitle.message}
                    </p>
                  )}
                </div>

                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h4 className="font-semibold text-gray-800 mb-2">
                    Report Description
                  </h4>
                  <textarea
                    placeholder="Enter report description"
                    className={`border ${
                      errors.reportDesc ? 'border-red-500' : 'border-gray-300'
                    } rounded-lg p-3 w-full text-base focus:border-orange-500 focus:ring-orange-500 focus:outline-none resize-vertical min-h-[120px]`}
                    {...register('reportDesc', {
                      required: 'Report description is required',
                    })}
                  />
                  {errors.reportDesc && (
                    <p className="mt-1 text-red-500 text-sm">
                      {errors.reportDesc.message}
                    </p>
                  )}
                </div>

                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h4 className="font-semibold text-gray-800 mb-2">
                    Report Price (₹)
                  </h4>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    placeholder="Enter report price"
                    className={`border ${
                      errors.reportPrice ? 'border-red-500' : 'border-gray-300'
                    } rounded-lg p-3 w-full text-base focus:border-orange-500 focus:ring-orange-500 focus:outline-none`}
                    {...register('reportPrice', {
                      required: 'Report price is required',
                      min: {
                        value: 0,
                        message: 'Price must be greater than or equal to 0',
                      },
                    })}
                  />
                  {errors.reportPrice && (
                    <p className="mt-1 text-red-500 text-sm">
                      {errors.reportPrice.message}
                    </p>
                  )}
                </div>

                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h4 className="font-semibold text-gray-800 mb-2">
                    Report File (PDF)
                  </h4>
                  {(pdfFile || pdfFileNew) && (
                    <div className="mt-4">
                      <p className="text-sm text-gray-700 mb-2">
                        Current file: {pdfFileNew ? pdfFileNew.name : pdfFile}
                      </p>
                      {pdfPreview && (
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                          <object
                            data={pdfPreview}
                            type="application/pdf"
                            className="w-full h-64"
                          >
                            <div className="p-4 bg-gray-50 text-center">
                              <p className="text-gray-600">
                                PDF preview not available
                              </p>
                              <a
                                href={pdfPreview}
                                target="_blank"
                                rel="noreferrer"
                                className="text-orange-500 hover:text-orange-600 font-medium mt-2 inline-block"
                              >
                                Open PDF in new tab
                              </a>
                            </div>
                          </object>
                        </div>
                      )}
                    </div>
                  )}
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handlePdfChange}
                    className="mt-3 block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-600 hover:file:bg-orange-100"
                    {...register('report', {
                      required: false,
                    })}
                  />
                  {errors.report && (
                    <p className="mt-1 text-red-500 text-sm">
                      {errors.report.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-8 text-center">
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white text-lg font-medium px-8 py-3 rounded-lg shadow-md transition-all disabled:cursor-not-allowed"
            >
              {updateMutation.isPending
                ? 'Updating Report...'
                : 'Update Report'}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

export default EditFinancialReport
